import { RefObject, useEffect, useRef } from 'react';

/**
 * Hook to handle active step scrolling behavior
 * Centers the active step once it reaches the middle position
 */
export const useActiveStepScroll = (
  containerRef: RefObject<HTMLElement | null>,
  activeStepIndex: number,
  stepCount: number,
  stepSpacing: number,
) => {
  // Track previous active step index to avoid unnecessary scrolls
  const prevActiveStepIndexRef = useRef<number>(activeStepIndex);
  const prevStepSpacingRef = useRef<number>(stepSpacing);

  useEffect(() => {
    if (!containerRef.current || stepCount === 0) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const middleStepIndex = Math.floor(stepCount / 2);

    // Calculate scroll position
    let scrollPosition = 0;

    if (activeStepIndex >= middleStepIndex) {
      // Center the active step in the container's visible area
      // Calculate the position to center the active step
      // Step centers: 0, stepSpacing, 2*stepSpacing, etc.
      // First step center is at nodeWidth/2, others at nodeWidth/2 + index * stepSpacing
      const nodeWidth = 64; // Approximate: STEP_NODE_CONSTANTS.SIZE * 8px
      const activeStepCenterPosition =
        nodeWidth / 2 + activeStepIndex * stepSpacing;
      const containerCenter = containerWidth / 2;
      scrollPosition = activeStepCenterPosition - containerCenter;

      // Ensure we don't scroll past the start
      scrollPosition = Math.max(0, scrollPosition);

      // Ensure we don't scroll past the end
      const maxScroll = container.scrollWidth - containerWidth;
      scrollPosition = Math.min(scrollPosition, maxScroll);
    } else {
      // Before reaching middle: scroll to left (position 0) to show first steps
      scrollPosition = 0;
    }

    // Only scroll if:
    // 1. Active step index changed, OR
    // 2. Step spacing changed (viewport resize)
    const shouldScroll =
      prevActiveStepIndexRef.current !== activeStepIndex ||
      prevStepSpacingRef.current !== stepSpacing;

    if (shouldScroll) {
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });

      prevActiveStepIndexRef.current = activeStepIndex;
      prevStepSpacingRef.current = stepSpacing;
    }
  }, [containerRef, activeStepIndex, stepCount, stepSpacing]);

  // Handle container resize (viewport resize or container size change)
  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      // Recalculate scroll position on resize
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const middleStepIndex = Math.floor(stepCount / 2);
      let scrollPosition = 0;

      if (activeStepIndex >= middleStepIndex) {
        // Step centers: first at nodeWidth/2, others at nodeWidth/2 + index * stepSpacing
        const nodeWidth = 64; // Approximate: STEP_NODE_CONSTANTS.SIZE * 8px
        const activeStepCenterPosition =
          nodeWidth / 2 + activeStepIndex * stepSpacing;
        const containerCenter = containerWidth / 2;
        scrollPosition = activeStepCenterPosition - containerCenter;
        scrollPosition = Math.max(0, scrollPosition);
        const maxScroll = container.scrollWidth - containerWidth;
        scrollPosition = Math.min(scrollPosition, maxScroll);
      }

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    };

    // Debounced resize handler
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    // Use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(debouncedHandleResize);

    resizeObserver.observe(containerRef.current);

    // Also listen to window resize for viewport changes
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', debouncedHandleResize);
      clearTimeout(timeoutId);
    };
  }, [containerRef, activeStepIndex, stepCount, stepSpacing]);
};
